import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { getDramaWithEpisodes, updateDrama } from "@/services/firebase/drama";
import { updateVideo } from "@/services/firebase/video";
import { getDramaEpisodeStats } from "@/services/firebase/transaction";
import { Drama, VideoMeta } from "@/types/video";

import {
  AdminPage,
  AdminLoadingSkeleton,
  AdminErrorAlert,
} from "@/components/admin-ui";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

const DEFAULT_EPISODE_COST = 30;

const pricingSchema = z.object({
  episode_unlock_coin_cost: z.number().int().min(1).nullable(),
  enable_full_drama_coin_purchase: z.boolean(),
  full_drama_coin_cost: z.number().int().min(0),
  free_episode_count: z.number().int().min(0),
});

type PricingFormData = z.infer<typeof pricingSchema>;

type DramaChangeRow = { label: string; from: string; to: string };

type EpisodeChangeItem = VideoMeta & { id: string; orderLabel: string };

type PricingDiff = {
  dramaChanges: DramaChangeRow[];
  episodeChanges: {
    toPaid: EpisodeChangeItem[];
    toFree: EpisodeChangeItem[];
  };
  projected: {
    episodeCost: number;
    episodeCount: number;
    perEpisodeTotal: number;
    bundleEnabled: boolean;
    bundleCost: number;
    discountPct: number | null;
  };
  warnings: string[];
  hasAnyChange: boolean;
};

export default function DramaPricingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: dramaData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["drama-with-episodes", id],
    queryFn: () => getDramaWithEpisodes(id!),
    enabled: !!id,
  });

  const drama = dramaData?.drama;
  const videos = useMemo(
    () =>
      [...(dramaData?.videos ?? [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      ),
    [dramaData]
  );

  const { data: episodeStats } = useQuery({
    queryKey: ["drama", id, "episode-stats"],
    queryFn: () => getDramaEpisodeStats(drama!.drama_id),
    enabled: !!drama?.drama_id,
  });

  const currentFreeCount = useMemo(() => {
    let count = 0;
    for (const v of videos) {
      if (v.vip) break;
      count += 1;
    }
    return count;
  }, [videos]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<PricingFormData>({
    resolver: zodResolver(pricingSchema),
    defaultValues: {
      episode_unlock_coin_cost: null,
      enable_full_drama_coin_purchase: false,
      full_drama_coin_cost: 0,
      free_episode_count: 0,
    },
  });

  useEffect(() => {
    if (!drama) return;
    reset({
      episode_unlock_coin_cost:
        drama.episode_unlock_coin_cost != null &&
        drama.episode_unlock_coin_cost > 0
          ? drama.episode_unlock_coin_cost
          : null,
      enable_full_drama_coin_purchase:
        !!drama.enable_full_drama_coin_purchase,
      full_drama_coin_cost: drama.full_drama_coin_cost || 0,
      free_episode_count: currentFreeCount,
    });
  }, [drama, currentFreeCount, reset]);

  const watchEpisodeCost = watch("episode_unlock_coin_cost");
  const watchBundleEnabled = watch("enable_full_drama_coin_purchase");
  const watchBundleCost = watch("full_drama_coin_cost");
  const watchFreeCount = watch("free_episode_count");

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [diff, setDiff] = useState<PricingDiff | null>(null);

  const mutation = useMutation({
    mutationFn: async (formValues: PricingFormData) => {
      if (!id || !drama) throw new Error("Drama not loaded");

      await updateDrama(id, {
        episode_unlock_coin_cost: formValues.episode_unlock_coin_cost,
        enable_full_drama_coin_purchase:
          formValues.enable_full_drama_coin_purchase,
        full_drama_coin_cost: formValues.enable_full_drama_coin_purchase
          ? formValues.full_drama_coin_cost
          : 0,
      });

      const vipUpdates: Promise<void>[] = [];
      videos.forEach((video, idx) => {
        if (!video.id) return;
        const targetVip = !(idx < formValues.free_episode_count);
        if (video.vip !== targetVip) {
          vipUpdates.push(updateVideo(video.id, { vip: targetVip }));
        }
      });
      if (vipUpdates.length > 0) {
        await Promise.all(vipUpdates);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["drama-with-episodes", id],
      });
      queryClient.invalidateQueries({ queryKey: ["drama", id] });
      queryClient.invalidateQueries({ queryKey: ["dramas"] });
      navigate(`/dramas/${id}`);
    },
    onError: (err: unknown) => {
      setSubmitError(
        err instanceof Error ? err.message : "儲存失敗，請稍後重試"
      );
    },
  });

  const onSubmit = (values: PricingFormData) => {
    setSubmitError(null);

    if (values.enable_full_drama_coin_purchase && values.full_drama_coin_cost <= 0) {
      setSubmitError("啟用全劇一次買斷時，金幣價格必須大於 0");
      return;
    }
    if (values.free_episode_count > videos.length) {
      setSubmitError(`免費集數不可超過本劇集數（${videos.length}）`);
      return;
    }

    const nextDiff = buildDiff({
      drama: drama!,
      videos,
      values,
      currentFreeCount,
    });
    setDiff(nextDiff);
  };

  const handleConfirm = () => {
    if (!diff) return;
    const values = {
      episode_unlock_coin_cost: watchEpisodeCost,
      enable_full_drama_coin_purchase: watchBundleEnabled,
      full_drama_coin_cost: watchBundleCost,
      free_episode_count: watchFreeCount,
    };
    mutation.mutate(values);
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  if (error || !drama) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={
            error instanceof Error ? error.message : "找不到劇集或載入失敗"
          }
        />
        <Button variant="outline" onClick={() => navigate("/dramas")}>
          返回列表
        </Button>
      </AdminPage>
    );
  }

  const episodeCountForPreview = videos.length;
  const effectiveEpisodeCost =
    watchEpisodeCost != null && watchEpisodeCost > 0
      ? watchEpisodeCost
      : DEFAULT_EPISODE_COST;
  const usingCustomEpisodeCost =
    watchEpisodeCost != null && watchEpisodeCost > 0;
  const perEpisodeTotal = effectiveEpisodeCost * episodeCountForPreview;
  const bundleCost = watchBundleEnabled ? watchBundleCost || 0 : 0;
  const bundleDiscountPct =
    watchBundleEnabled && perEpisodeTotal > 0 && bundleCost > 0
      ? Math.round(((perEpisodeTotal - bundleCost) / perEpisodeTotal) * 100)
      : null;
  const bundleWarning =
    watchBundleEnabled && perEpisodeTotal > 0 && bundleCost >= perEpisodeTotal
      ? `全劇買斷金幣（${bundleCost}）不低於單集累計（${perEpisodeTotal}），用戶沒有折扣誘因。`
      : null;

  return (
    <AdminPage maxWidth="narrow">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/dramas/${id}`}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          返回 劇集詳情
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">
              定價設定
            </h1>
            <p className="mt-1 text-sm text-muted-foreground truncate">
              {drama.drama_title || "(未命名)"} ·{" "}
              <span className="font-mono">{drama.drama_id}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/dramas/${id}`)}
              disabled={mutation.isPending}
            >
              取消
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "儲存中…" : "儲存變更"}
            </Button>
          </div>
        </div>
      </div>

      {submitError && (
        <AdminErrorAlert
          message={submitError}
          onDismiss={() => setSubmitError(null)}
        />
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        noValidate
      >
        {/* Section 1 — 單集解鎖 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle>單集解鎖金幣</CardTitle>
                <CardDescription>
                  用戶每解鎖一集需消耗的金幣數。留空等於跟隨系統預設。
                </CardDescription>
              </div>
              <Badge variant={usingCustomEpisodeCost ? "default" : "secondary"}>
                {usingCustomEpisodeCost ? "自訂價" : "系統預設"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="episode_unlock_coin_cost">每集金幣</Label>
                <Input
                  id="episode_unlock_coin_cost"
                  type="number"
                  min={1}
                  placeholder={`留空 = 使用系統預設 ${DEFAULT_EPISODE_COST} 金幣`}
                  {...register("episode_unlock_coin_cost", {
                    setValueAs: (v) => {
                      if (v === "" || v === null || v === undefined) return null;
                      const num = Number(v);
                      return Number.isNaN(num) || num <= 0
                        ? null
                        : Math.floor(num);
                    },
                  })}
                />
                {errors.episode_unlock_coin_cost && (
                  <p className="text-xs text-destructive">
                    {errors.episode_unlock_coin_cost.message}
                  </p>
                )}
              </div>
              {usingCustomEpisodeCost && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="sm:self-end"
                  onClick={() =>
                    setValue("episode_unlock_coin_cost", null, {
                      shouldDirty: true,
                    })
                  }
                >
                  還原為系統預設
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              系統預設為 {DEFAULT_EPISODE_COST} 金幣。留空時本劇跟隨系統預設；日後調整系統預設，本劇會一併跟隨。
            </p>
          </CardContent>
        </Card>

        {/* Section 2 — 全劇一次買斷 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle>全劇一次買斷</CardTitle>
                <CardDescription>
                  啟用後，用戶可以一次付費解鎖整部劇（僅支援金幣）。
                </CardDescription>
              </div>
              <Switch
                checked={watchBundleEnabled}
                onCheckedChange={(checked) =>
                  setValue("enable_full_drama_coin_purchase", checked, {
                    shouldDirty: true,
                  })
                }
              />
            </div>
          </CardHeader>
          {watchBundleEnabled && (
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="full_drama_coin_cost">全劇金幣價格</Label>
                <Input
                  id="full_drama_coin_cost"
                  type="number"
                  min={0}
                  placeholder="例：450"
                  {...register("full_drama_coin_cost", {
                    setValueAs: (v) => {
                      if (v === "" || v === null || v === undefined) return 0;
                      const num = Number(v);
                      return Number.isNaN(num) || num < 0
                        ? 0
                        : Math.floor(num);
                    },
                  })}
                />
                {errors.full_drama_coin_cost && (
                  <p className="text-xs text-destructive">
                    {errors.full_drama_coin_cost.message}
                  </p>
                )}
              </div>
              {bundleWarning && (
                <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                  {bundleWarning}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Section 3 — 免費集數 */}
        <Card>
          <CardHeader>
            <CardTitle>免費集數（前 N 集）</CardTitle>
            <CardDescription>
              前 N 集開放免費觀看，其餘集數為付費鎖集。儲存時會同步更新每集的付費狀態。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {videos.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                本劇尚未建立集數，請先於集數管理頁新增後再設定免費集數。
              </p>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="space-y-1.5 flex-1">
                    <Label htmlFor="free_episode_count">免費集數</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="free_episode_count"
                        type="number"
                        min={0}
                        max={videos.length}
                        className="w-32"
                        {...register("free_episode_count", {
                          setValueAs: (v) => {
                            if (v === "" || v === null || v === undefined) return 0;
                            const num = Number(v);
                            if (Number.isNaN(num) || num < 0) return 0;
                            return Math.min(videos.length, Math.floor(num));
                          },
                        })}
                      />
                      <span className="text-sm text-muted-foreground">
                        集（本劇共 {videos.length} 集）
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
                  <div className="font-medium text-foreground">套用後：</div>
                  <div className="flex items-center gap-2">
                    <Badge variant="success" className="text-[10px]">
                      免費
                    </Badge>
                    <span className="text-muted-foreground">
                      {watchFreeCount > 0
                        ? `第 1 ~ ${watchFreeCount} 集（共 ${watchFreeCount} 集）`
                        : "無"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="warning" className="text-[10px]">
                      付費
                    </Badge>
                    <span className="text-muted-foreground">
                      {watchFreeCount >= videos.length
                        ? "無"
                        : `第 ${watchFreeCount + 1} ~ ${videos.length} 集（共 ${videos.length - watchFreeCount} 集）`}
                    </span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Section 4 — 現況摘要（唯讀） */}
        <Card>
          <CardHeader>
            <CardTitle>現況摘要</CardTitle>
            <CardDescription>
              下列為劇集的目前狀態，僅供參考，不會因表單變更而更新。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <SummaryField label="本劇集數" value={`${videos.length} 集`} />
              <SummaryField
                label="目前單集價"
                value={
                  drama.episode_unlock_coin_cost &&
                  drama.episode_unlock_coin_cost > 0
                    ? `${drama.episode_unlock_coin_cost} 金幣`
                    : `${DEFAULT_EPISODE_COST} 金幣（預設）`
                }
              />
              <SummaryField
                label="目前全劇買斷"
                value={
                  drama.enable_full_drama_coin_purchase
                    ? `${(drama.full_drama_coin_cost || 0).toLocaleString()} 金幣`
                    : "未啟用"
                }
              />
              <SummaryField
                label="目前免費集數"
                value={`前 ${currentFreeCount} 集`}
              />
              <SummaryField
                label="累計解鎖收益"
                value={`${(episodeStats?.totalUnlockRevenue ?? 0).toLocaleString()} 金幣`}
              />
              <SummaryField
                label="全劇買斷用戶"
                value={`${(episodeStats?.fullDramaUnlockCount ?? 0).toLocaleString()} 人`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer action bar */}
        <Separator />
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/dramas/${id}`)}
            disabled={mutation.isPending}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending || !isDirty}>
            {mutation.isPending ? "儲存中…" : "儲存變更"}
          </Button>
        </div>
      </form>

      {/* 試算確認 Dialog */}
      <AlertDialog
        open={!!diff}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) setDiff(null);
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>確認定價變更</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            {diff && (
              <>
                {/* Section A — 劇集定價異動 */}
                <DiffSection title="劇集定價異動">
                  {diff.dramaChanges.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      無定價異動。
                    </p>
                  ) : (
                    <div className="rounded-md border">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">
                              項目
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              原值
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              新值
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {diff.dramaChanges.map((row) => (
                            <tr key={row.label} className="border-t">
                              <td className="px-3 py-2 font-medium text-foreground">
                                {row.label}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground tabular-nums">
                                {row.from}
                              </td>
                              <td className="px-3 py-2 text-foreground tabular-nums">
                                {row.to}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </DiffSection>

                {/* Section B — 集數狀態變更 */}
                <DiffSection title="集數狀態變更">
                  {diff.episodeChanges.toPaid.length === 0 &&
                  diff.episodeChanges.toFree.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      無集數狀態異動。
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {diff.episodeChanges.toFree.length > 0 && (
                        <EpisodeChangeList
                          heading={`轉為免費（${diff.episodeChanges.toFree.length} 集）`}
                          items={diff.episodeChanges.toFree}
                          fromLabel="付費"
                          toLabel="免費"
                          toVariant="success"
                        />
                      )}
                      {diff.episodeChanges.toPaid.length > 0 && (
                        <EpisodeChangeList
                          heading={`轉為付費（${diff.episodeChanges.toPaid.length} 集）`}
                          items={diff.episodeChanges.toPaid}
                          fromLabel="免費"
                          toLabel="付費"
                          toVariant="warning"
                        />
                      )}
                    </div>
                  )}
                </DiffSection>

                {/* Section C — 試算結果 */}
                <DiffSection title="試算結果">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <StatBlock
                      label="單集金幣"
                      value={`${diff.projected.episodeCost}`}
                    />
                    <StatBlock
                      label="本劇集數"
                      value={`${diff.projected.episodeCount}`}
                    />
                    <StatBlock
                      label="逐集累計"
                      value={
                        diff.projected.perEpisodeTotal > 0
                          ? `${diff.projected.perEpisodeTotal}`
                          : "—"
                      }
                    />
                    <StatBlock
                      label="全劇買斷"
                      value={
                        diff.projected.bundleEnabled
                          ? `${diff.projected.bundleCost}${
                              diff.projected.discountPct != null &&
                              diff.projected.discountPct > 0
                                ? ` · 省 ${diff.projected.discountPct}%`
                                : ""
                            }`
                          : "未啟用"
                      }
                    />
                  </div>
                </DiffSection>

                {/* Section D — 注意事項 */}
                {diff.warnings.length > 0 && (
                  <DiffSection title="注意事項">
                    <div className="space-y-2">
                      {diff.warnings.map((w, i) => (
                        <div
                          key={i}
                          className="rounded-md border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
                        >
                          {w}
                        </div>
                      ))}
                    </div>
                  </DiffSection>
                )}

                {!diff.hasAnyChange && (
                  <p className="text-sm text-muted-foreground">
                    本次未偵測到任何變更，可直接按「取消」返回或按「確認並儲存」無影響送出。
                  </p>
                )}
              </>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "儲存中…" : "確認並儲存"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
}

// ── Helpers ────────────────────────────────────────────────

function SummaryField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium text-foreground tabular-nums">{value}</div>
    </div>
  );
}

function DiffSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function EpisodeChangeList({
  heading,
  items,
  fromLabel,
  toLabel,
  toVariant,
}: {
  heading: string;
  items: EpisodeChangeItem[];
  fromLabel: string;
  toLabel: string;
  toVariant: "success" | "warning";
}) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">
        {heading}
      </div>
      <div className="rounded-md border">
        <ul className="divide-y">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground w-10 tabular-nums">
                  {item.orderLabel}
                </span>
                <span className="truncate">
                  {item.caption || item.name || "(未命名)"}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="secondary" className="text-[10px]">
                  {fromLabel}
                </Badge>
                <span className="text-xs text-muted-foreground">→</span>
                <Badge variant={toVariant} className="text-[10px]">
                  {toLabel}
                </Badge>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Diff builder ───────────────────────────────────────────

function buildDiff({
  drama,
  videos,
  values,
  currentFreeCount,
}: {
  drama: Drama;
  videos: (VideoMeta & { id: string })[];
  values: PricingFormData;
  currentFreeCount: number;
}): PricingDiff {
  const dramaChanges: DramaChangeRow[] = [];

  // 單集價
  const currentEpCost =
    drama.episode_unlock_coin_cost != null && drama.episode_unlock_coin_cost > 0
      ? drama.episode_unlock_coin_cost
      : null;
  const newEpCost = values.episode_unlock_coin_cost;
  if (currentEpCost !== newEpCost) {
    dramaChanges.push({
      label: "單集解鎖金幣",
      from:
        currentEpCost != null
          ? `${currentEpCost} 金幣`
          : `預設 ${DEFAULT_EPISODE_COST} 金幣`,
      to:
        newEpCost != null
          ? `${newEpCost} 金幣`
          : `預設 ${DEFAULT_EPISODE_COST} 金幣`,
    });
  }

  // 全劇買斷啟用
  const currentBundle = !!drama.enable_full_drama_coin_purchase;
  const newBundle = values.enable_full_drama_coin_purchase;
  if (currentBundle !== newBundle) {
    dramaChanges.push({
      label: "全劇一次買斷",
      from: currentBundle ? "已啟用" : "未啟用",
      to: newBundle ? "已啟用" : "未啟用",
    });
  }

  // 全劇買斷價格（只在啟用狀態下比對）
  const currentBundleCost = drama.full_drama_coin_cost || 0;
  const newBundleCost = newBundle ? values.full_drama_coin_cost : 0;
  if (newBundle && currentBundleCost !== newBundleCost) {
    dramaChanges.push({
      label: "全劇買斷金幣",
      from: currentBundle ? `${currentBundleCost} 金幣` : "—",
      to: `${newBundleCost} 金幣`,
    });
  }

  // 免費集數
  if (currentFreeCount !== values.free_episode_count) {
    dramaChanges.push({
      label: "免費集數",
      from: `前 ${currentFreeCount} 集`,
      to: `前 ${values.free_episode_count} 集`,
    });
  }

  // 集數狀態變更
  const toPaid: EpisodeChangeItem[] = [];
  const toFree: EpisodeChangeItem[] = [];
  videos.forEach((video, idx) => {
    const shouldBeFree = idx < values.free_episode_count;
    const targetVip = !shouldBeFree;
    if (video.vip === targetVip) return;
    const item: EpisodeChangeItem = {
      ...video,
      orderLabel: String(video.order ?? idx + 1),
    };
    if (shouldBeFree) toFree.push(item);
    else toPaid.push(item);
  });

  // 試算
  const episodeCostForProjection =
    newEpCost != null && newEpCost > 0 ? newEpCost : DEFAULT_EPISODE_COST;
  const perEpisodeTotal = episodeCostForProjection * videos.length;
  const discountPct =
    newBundle && perEpisodeTotal > 0 && newBundleCost > 0
      ? Math.round(((perEpisodeTotal - newBundleCost) / perEpisodeTotal) * 100)
      : null;

  // Warnings
  const warnings: string[] = [];
  if (newBundle && perEpisodeTotal > 0 && newBundleCost >= perEpisodeTotal) {
    warnings.push(
      `全劇買斷金幣（${newBundleCost}）不低於單集累計（${perEpisodeTotal}），用戶沒有折扣誘因。`
    );
  }
  if (videos.length > 0 && values.free_episode_count === 0) {
    warnings.push("免費集數為 0：沒有免費試看，可能影響引流。");
  }
  if (videos.length > 0 && values.free_episode_count >= videos.length) {
    warnings.push("免費集數已涵蓋全劇：全部集數免費，無法產生付費解鎖收益。");
  }
  if (
    currentEpCost != null &&
    newEpCost != null &&
    Math.abs(newEpCost - currentEpCost) / currentEpCost > 1
  ) {
    warnings.push(
      `單集金幣由 ${currentEpCost} 變更為 ${newEpCost}（變動超過 100%），請再次確認是否為誤填。`
    );
  }

  return {
    dramaChanges,
    episodeChanges: { toPaid, toFree },
    projected: {
      episodeCost: episodeCostForProjection,
      episodeCount: videos.length,
      perEpisodeTotal,
      bundleEnabled: newBundle,
      bundleCost: newBundleCost,
      discountPct,
    },
    warnings,
    hasAnyChange:
      dramaChanges.length > 0 ||
      toPaid.length > 0 ||
      toFree.length > 0,
  };
}
